import React, { useCallback, useState } from "react";

import Col from "antd/es/col";
import Row from "antd/es/row";
import { useIntl } from "react-intl";

import { EntityType } from "@mapmycustomers/shared/enum";
import { Button, Modal } from "@mapmycustomers/ui";

import Checkbox from "@app/component/input/Checkbox";
import layout from "@app/styles/layout";
import { getEntityTypeDisplayName } from "@app/util/ui";

import styles from "./ExportListDataConfirmModal.module.scss";

interface Props {
  entityType: EntityType;
  onConfirm: (dontShowAgain: boolean) => void;
  onHide: () => void;
}

const ExportListDataConfirmModal: React.FC<Props> = ({ entityType, onConfirm, onHide }) => {
  const intl = useIntl();

  const [dontShowAgain, setDontShowAgain] = useState<boolean>(false);

  const handleConfirm = useCallback(() => {
    onConfirm(dontShowAgain);
    onHide();
  }, [dontShowAgain, onConfirm, onHide]);

  return (
    <Modal
      footer={null}
      onCancel={onHide}
      title={intl.formatMessage({
        id: "exportListDataConfirmationModal.title",
        defaultMessage: "Confirm export",
        description: "Confirmation list data modal - title",
      })}
      visible
    >
      <div className={styles.container}>
        {entityType !== EntityType.LEADERBOARD ? (
          <>
            <div>
              <div>
                {intl.formatMessage({
                  id: "exportListDataConfirmationModal.text",
                  defaultMessage: "This export will include",
                  description: "Confirmation list data modal - text",
                })}
              </div>
              <div>
                <ul>
                  <li>
                    {intl.formatMessage({
                      id: "exportListDataConfirmationModal.point1",
                      defaultMessage: "Any columns currently selected",
                      description: "Confirmation list data modal - point 1",
                    })}
                  </li>
                  <li>
                    {intl.formatMessage({
                      id: "exportListDataConfirmationModal.point2",
                      defaultMessage: "Any filters currently applied",
                      description: "Confirmation list data modal - point 2",
                    })}
                  </li>
                </ul>
              </div>
            </div>
            <div className={styles.description}>
              {intl.formatMessage({
                id: "exportListDataConfirmationModal.description",
                defaultMessage:
                  "Please note that tables with >10k records will be sent to your email.",
                description: "Confirmation list data modal - description",
              })}
            </div>
          </>
        ) : (
          <div>
            {intl.formatMessage({
              id: "exportListDataConfirmationModal.leaderboardText",
              defaultMessage:
                "This export will include all data currently visible in table. Columns not visible will be ignored.",
              description: "Confirmation list data modal for leaderboard - text",
            })}
          </div>
        )}
        <Row align="middle" justify="space-between">
          <Col>
            <Checkbox checked={dontShowAgain} onChange={setDontShowAgain}>
              {intl.formatMessage({
                id: "exportListDataConfirmationModal.dontShowAgain",
                defaultMessage: "Don’t show again",
                description: "Confirmation list data modal - don't show again text",
              })}
            </Checkbox>
          </Col>
          <Col>
            <Row gutter={layout.spacerM}>
              <Col>
                <Button onClick={onHide}>
                  {intl.formatMessage({
                    id: "exportListDataConfirmationModal.cancel",
                    defaultMessage: "Cancel",
                    description: "Confirmation list data modal - don't show again text",
                  })}
                </Button>
              </Col>
              <Col>
                <Button onClick={handleConfirm} type="primary">
                  {intl.formatMessage(
                    {
                      id: "exportListDataConfirmationModal.confirm",
                      defaultMessage: "Export {entity}",
                      description: "Confirmation list data modal - don't show again text",
                    },
                    {
                      entity: getEntityTypeDisplayName(intl, entityType, {
                        lowercase: false,
                        plural: true,
                      }),
                    }
                  )}
                </Button>
              </Col>
            </Row>
          </Col>
        </Row>
      </div>
    </Modal>
  );
};

export default ExportListDataConfirmModal;
